import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { UserRoleSelect } from '../common/form/UserRoleSelect';
import { SectionHeader } from '../common/SectionHeader';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../common/basic/QcsModalBox';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { UserRole } from '@api/models/enums/UserRole';
import { CreateUserFromEmployeeRequest } from '@api/models/user/CreateUserFromEmployeeRequest';
import { employeeService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';

interface Props {
  open: boolean;
  onClose: () => void;
  reloadData?: () => void;
  employeeId?: string;
}

export const CreateUserModal: FC<Props> = ({ open, onClose, reloadData, employeeId: employeeIdProp }) => {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const companyCustomization = useAppSelector(selectCompanyCustomization);

  const handleSubmit = async (data: CreateUserFromEmployeeRequest) => {
    await employeeService.createUser(employeeIdProp ?? employeeId!, data);
    reloadData?.();
    onClose();
  };

  const validate = (values: CreateUserFromEmployeeRequest) => {
    const errors: Partial<Record<keyof CreateUserFromEmployeeRequest, string>> = {};

    if (
      values.roles.some((role) => role === UserRole.Tourniquet || role === UserRole.TourniquetTraining || role === UserRole.Training) &&
      values.roles.length !== 1
    ) {
      errors.roles = t('user.errorInvalidRoleCombination') ?? undefined;
    }

    return errors;
  };

  return (
    <QcsModal open={open} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title="employee.createUserTitle" />
        <FormikDebounce<CreateUserFromEmployeeRequest>
          initialValues={{
            roles: [],
            language: companyCustomization?.defaultLanguage ?? '',
          }}
          validationSchema={Yup.object().shape({
            roles: validations.arrayNotEmpty(t),
            language: validations.stringRequired(t),
          })}
          validate={validate}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <UserRoleSelect name="roles" label={t('user.roles')} required />
              <LanguageSelect name={'language'} label={t('user.defaultLanguage')} languages={SUPPORTED_LANGUAGES} required />

              <ShowError resource={resources.createUser} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
